import { adminTenantId, type ConsentInfoResponse } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import useNativeSignIn, { getOverseasBrand } from '@/hooks/use-native-sign-in';
import edge0Wordmark from '@/shared/assets/edge0.svg?url';
import nativeWordmark from '@/shared/assets/eva-wordmark.png';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type ThirdPartyBranding = ConsentInfoResponse['application']['branding'];

type Props = {
  readonly children: ReactNode;
  readonly title: TFuncKey;
  readonly titleInterpolation?: Record<string, unknown>;
  readonly thirdPartyBranding?: ThirdPartyBranding;
};

const LandingPageLayout = ({ children, title, titleInterpolation, thirdPartyBranding }: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);
  const native = useNativeSignIn();
  const useNativeEvaTheme = native && experienceSettings?.tenantId !== adminTenantId;
  const brand = getOverseasBrand(experienceSettings?.customContent.evaEdition);
  const overseasLogo = brand === 'edge0' ? edge0Wordmark : nativeWordmark;

  if (!experienceSettings) {
    return null;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title, titleKeyInterpolation: titleInterpolation }}>
      <BrandingHeader
        className={classNames(
          styles.header,
          layoutClassNames.brandingHeader,
          useNativeEvaTheme && brand === 'edge0' && styles.edge0Brand
        )}
        headline={title}
        headlineInterpolation={titleInterpolation}
        logo={
          useNativeEvaTheme
            ? overseasLogo
            : getBrandingLogoUrl({ theme, branding, isDarkModeEnabled })
        }
        thirdPartyLogo={
          thirdPartyBranding &&
          getBrandingLogoUrl({ theme, branding: thirdPartyBranding, isDarkModeEnabled })
        }
      />
      {children}
    </FirstScreenLayout>
  );
};

export default LandingPageLayout;
